import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";

interface ProfileMiniMapProps {
  coords: [number, number];
  className?: string;
}

export function ProfileMiniMap({ coords, className }: ProfileMiniMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;

    const initMap = async () => {
      const container = mapRef.current;
      if (!container) return;

      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      if ((container as HTMLDivElement & { _leaflet_id?: number })._leaflet_id) {
        delete (container as HTMLDivElement & { _leaflet_id?: number })._leaflet_id;
        container.innerHTML = "";
      }

      const leaflet = await import("leaflet");
      const L = leaflet.default || leaflet;
      if (cancelled || !mapRef.current) return;

      if (L.Icon && L.Icon.Default) {
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
          iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
          shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
        });
      }

      const map = L.map(container, {
        center: coords,
        zoom: 10,
        dragging: true,
        touchZoom: true,
        doubleClickZoom: true,
        scrollWheelZoom: false,
        boxZoom: true,
        keyboard: true,
        zoomControl: true,
        attributionControl: false,
      });
      leafletMapRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(map);
      L.marker(coords).addTo(map);
    };

    initMap();
    return () => {
      cancelled = true;
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      if (mapRef.current && (mapRef.current as HTMLDivElement & { _leaflet_id?: number })._leaflet_id) {
        delete (mapRef.current as HTMLDivElement & { _leaflet_id?: number })._leaflet_id;
        mapRef.current.innerHTML = "";
      }
    };
  }, [coords]);

  return (
    <div
      ref={mapRef}
      className={className ?? "h-44 w-full overflow-hidden rounded-lg border border-border"}
      style={{ isolation: "isolate" }}
    />
  );
}
